import { Link } from "react-router-dom";
import LegalLayout, { LegalSection } from "@/components/LegalLayout";
import { OWNER } from "@/lib/legalContent";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { LEGAL } from "@/constants/testIds";

const CookieTable = ({ rows }) => (
  <div className="overflow-x-auto rounded-xl border border-border">
    <table className="w-full min-w-[560px] text-left text-sm">
      <thead className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
        <tr>
          <th className="px-4 py-3 font-semibold">Cookie</th>
          <th className="px-4 py-3 font-semibold">Titular / Proveedor</th>
          <th className="px-4 py-3 font-semibold">Finalidad</th>
          <th className="px-4 py-3 font-semibold">Duración</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((r) => (
          <tr key={r.name}>
            <td className="px-4 py-3 font-mono text-xs font-semibold text-foreground">{r.name}</td>
            <td className="px-4 py-3">{r.owner}</td>
            <td className="px-4 py-3 text-muted-foreground">{r.purpose}</td>
            <td className="px-4 py-3 whitespace-nowrap">{r.duration}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PoliticaCookies() {
  const { openPreferences } = useCookieConsent();

  return (
    <LegalLayout
      title="Política de Cookies"
      intro="Información sobre las cookies y tecnologías similares que utiliza realmente este sitio web, conforme a la LSSI, el RGPD y la LOPDGDD."
    >
      <LegalSection heading="¿Qué son las cookies?">
        <p>
          Las cookies y otras tecnologías similares (como el almacenamiento local del navegador o los píxeles) son
          herramientas que los sitios web utilizan para almacenar y recuperar información sobre la navegación. Permiten,
          por ejemplo, recordar tus preferencias o medir de forma agregada cómo se utiliza la web.
        </p>
      </LegalSection>

      <LegalSection heading="Consentimiento">
        <p>
          Conforme a la normativa vigente, es obligatorio obtener el consentimiento del usuario antes de instalar
          cookies que no sean estrictamente necesarias. Por ello, al acceder por primera vez a la web te mostramos un
          banner desde el que puedes <strong>aceptar todas</strong>, <strong>rechazar</strong> las no necesarias o{" "}
          <strong>configurar</strong> tus preferencias. Las cookies analíticas permanecen desactivadas hasta que las
          aceptas expresamente.
        </p>
        <button
          onClick={openPreferences}
          data-testid={LEGAL.cookieConfigure}
          className="mt-2 inline-flex rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-transform hover:-translate-y-0.5"
        >
          Abrir panel de configuración de cookies
        </button>
      </LegalSection>

      <LegalSection heading="Tipos de cookies según su finalidad">
        <ul className="list-disc space-y-1.5 pl-5">
          <li><strong>Técnicas y funcionales:</strong> permiten la navegación y el uso de las funcionalidades de la web.</li>
          <li><strong>Analíticas:</strong> permiten medir y analizar de forma agregada el comportamiento de los usuarios para mejorar la web.</li>
        </ul>
        <p>
          Según la propiedad, las cookies pueden ser <strong>propias</strong> (gestionadas por el titular de la web) o
          <strong> de terceros</strong> (gestionadas por otra entidad). Según su duración, pueden ser <strong>de
          sesión</strong> (se eliminan al cerrar el navegador) o <strong>persistentes</strong> (permanecen durante un
          plazo determinado).
        </p>
      </LegalSection>

      <LegalSection heading="Cookies que utiliza realmente esta web">
        <p>
          Este sitio web es una aplicación moderna que <strong>no</strong> incorpora publicidad, píxeles de marketing,
          mapas embebidos, vídeos embebidos, chats de terceros ni redes de anuncios. Las tecnologías realmente presentes
          son las siguientes:
        </p>

        <h3 className="pt-2 font-display text-xl font-semibold text-foreground">1. Cookies técnicas / necesarias (propias)</h3>
        <p className="text-sm text-muted-foreground">Están siempre activas y no requieren consentimiento.</p>
        <CookieTable
          rows={[
            {
              name: "lcg_cookie_consent_v1",
              owner: "Propia (este sitio web)",
              purpose: "Almacena tus preferencias de consentimiento de cookies para no volver a mostrarte el banner.",
              duration: "Persistente (hasta que la elimines)",
            },
          ]}
        />

        <h3 className="pt-4 font-display text-xl font-semibold text-foreground">2. Cookies analíticas (de terceros)</h3>
        <p className="text-sm text-muted-foreground">
          Desactivadas por defecto. Solo se activan si prestas tu consentimiento a las cookies analíticas.
        </p>
        <CookieTable
          rows={[
            {
              name: "ph_* (PostHog)",
              owner: "PostHog (proveedor de la plataforma)",
              purpose: "Análisis agregado y anónimo del uso de la web para mejorar la experiencia. Solo si aceptas las cookies analíticas.",
              duration: "Hasta 1 año",
            },
          ]}
        />

        <h3 className="pt-4 font-display text-xl font-semibold text-foreground">3. Cookies de marketing o publicitarias</h3>
        <p className="rounded-xl border border-border bg-secondary/40 p-4 text-sm">
          Esta web <strong>no utiliza</strong> cookies de marketing, publicidad, seguimiento comercial ni redes de
          anuncios de ningún tipo.
        </p>
      </LegalSection>

      <LegalSection heading="Recursos externos">
        <p>
          Para su correcto funcionamiento y presentación, la web carga tipografías desde Google Fonts e imágenes desde
          servicios de alojamiento de imágenes. Estos recursos pueden implicar la conexión a servidores de terceros, pero
          no se utilizan para instalar cookies de seguimiento en tu dispositivo. Los enlaces a WhatsApp o teléfono solo
          se activan cuando haces clic en ellos.
        </p>
      </LegalSection>

      <LegalSection heading="Tratamiento de datos personales">
        <p>
          {OWNER.name} es la responsable del tratamiento de los datos personales que pudieran recabarse mediante cookies.
          La legitimación se basa en tu consentimiento (art. 6.1.a RGPD), salvo para las cookies necesarias para la
          navegación. Tienes derecho a retirar el consentimiento en cualquier momento, así como a los derechos de
          acceso, rectificación, supresión, portabilidad, limitación y oposición, y a presentar una reclamación ante la
          Agencia Española de Protección de Datos (<a className="text-primary underline underline-offset-2" href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a>).
          Puedes ejercerlos escribiendo a {OWNER.address} o a{" "}
          <a className="text-primary underline underline-offset-2" href={`mailto:${OWNER.email}`}>{OWNER.email}</a>. Consulta también nuestra{" "}
          <Link to="/politica-de-privacidad" className="text-primary underline underline-offset-2">Política de Privacidad</Link>.
        </p>
      </LegalSection>

      <LegalSection heading="Cómo gestionar las cookies desde el navegador">
        <p>
          Además del panel de configuración de esta web, puedes gestionar, bloquear o eliminar las cookies desde la
          configuración de tu navegador. Ten en cuenta que bloquear determinadas cookies puede afectar al funcionamiento
          de algunos servicios.
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><a className="text-primary underline underline-offset-2" href="http://support.google.com/chrome/answer/95647?hl=es" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
          <li><a className="text-primary underline underline-offset-2" href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
          <li><a className="text-primary underline underline-offset-2" href="https://www.mozilla.org/es-ES/privacy/websites/#cookies" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
          <li><a className="text-primary underline underline-offset-2" href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
          <li><a className="text-primary underline underline-offset-2" href="https://help.opera.com/en/latest/security-and-privacy/#clearBrowsingData" target="_blank" rel="noopener noreferrer">Opera</a></li>
        </ul>
      </LegalSection>
    </LegalLayout>
  );
}
